import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import cn from 'classnames';
import { Collapse } from 'react-bootstrap';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import * as R from 'ramda';
import { ApprovalStages } from '@blisspointmedia/bpm-types/dist/CreativeApprovals';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { FilterBar } from '@Components/FilterBar';
import { Button } from '@Components/Button/Button';
import { Img } from '@Components/Img';
import { InteractiveDiv } from '@Components/InteractiveEl/InteractiveEl';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { getCreativeThumbnail } from '@Components/MetricsTable/metrics-table-utils';
import { PieChart } from '@Components/PieChart';
import { CometProgressDialog } from '@Components/CometProgressDialog';
import { CreativeMap, MediaType, useCreativeMap } from '../../redux/creative';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useStateFunction } from '../../utils/hooks/useData';
import { dateToLongDate } from '../../utils/date-utils';
import {
  awaitJSON,
  COMET_WORKER_TIMEOUT_SECONDS,
  CometProgress,
  StreamingLambdaFetch,
  FlashtalkingLambdaFetch,
  fetchCometSelfInvoke
} from '../../utils/fetch-utils';
import { Placement, StreamingCreativesContext, TabKey } from './streaming-creatives-constants';

const CURRENT_DATE_LABEL = 'Current';
const CDN_NETWORK_LINK = 'https://cdn.blisspointmedia.com/networks/';

// Module-level constant so it's a stable reference across renders — passing a fresh array
// literal to useCreativeMap on every render forces its internal creativeMap memo to recompute
// the entire company dataset from scratch each time.
const GENERATE_MEDIA_TYPES: MediaType[] = ['streaming', 'audio', 'display'];

const DIMENSIONS = {
  DATE: 'Date',
  NETWORK: 'Network',
  NETWORK_GROUP: 'Network Group',
  ROTATION: 'Creative Rotation',
  ISCI: 'ISCI',
  CREATIVE: 'Creative Name'
} as const;

const GROUPING_OPTIONS = {
  DATE: 'Date',
  NETWORK: 'Network',
  CREATIVE: 'Creative'
} as const;
type GroupingKey = keyof typeof GROUPING_OPTIONS;

const PRESETS: Record<string, string[]> = {
  [GROUPING_OPTIONS.DATE]: [DIMENSIONS.DATE, DIMENSIONS.ROTATION, DIMENSIONS.NETWORK],
  [GROUPING_OPTIONS.NETWORK]: [
    DIMENSIONS.NETWORK_GROUP,
    DIMENSIONS.NETWORK,
    DIMENSIONS.DATE,
    DIMENSIONS.ROTATION
  ],
  [GROUPING_OPTIONS.CREATIVE]: [
    DIMENSIONS.CREATIVE,
    DIMENSIONS.DATE,
    DIMENSIONS.ROTATION,
    DIMENSIONS.NETWORK
  ]
};

const SORTERS: Record<string, (keys: string[]) => string[]> = {
  [DIMENSIONS.DATE]: R.sort(
    R.comparator((a: string, b: string) => {
      if (a === CURRENT_DATE_LABEL) {
        return true;
      }
      if (b === CURRENT_DATE_LABEL) {
        return false;
      }
      return a < b;
    })
  )
};

const FILTER_BAR_OPTIONS = [
  { label: 'Date', name: 'date' },
  { label: 'Network', name: 'network_label' },
  { label: 'Network Group', name: 'network_group' },
  { label: 'ISCI', name: 'isci' },
  { label: 'Creative Name', name: 'creative' },
  { label: 'Percentage', name: 'pct' },
  { label: 'Placement Name', name: 'placementName' }
];

interface LineMeta {
  date: string;
  network: string;
  network_label?: string;
  network_group?: string;
  isci: string;
  creative: string;
  pct: number;
  placementName: string;
}

interface GroupableLine {
  placementID: string;
  isci: string;
  pct: number;
  date: string;
}

type GroupedData = string[] | { [key: string]: GroupedData };

const groupBy =
  <T,>(fn: (item: T) => string) =>
  (list: T[]): Record<string, T[]> => {
    const out: Record<string, T[]> = {};
    for (const item of list) {
      (out[fn(item)] ||= []).push(item);
    }
    return out;
  };

type Groupers = Record<string, (values: GroupableLine[]) => Record<string, GroupableLine[]>>;

const makeRotationKey = (creatives: { isci: string; pct: number }[]): string =>
  [...creatives]
    .sort((a, b) => a.isci.localeCompare(b.isci))
    .map(({ isci, pct }) => `${isci}_${pct}`)
    .join('|');

const groupData = (values: GroupableLine[], types: string[], groupers: Groupers): GroupedData => {
  if (!types.length) {
    return [...new Set(values.map(value => value.placementID))];
  }
  const [type, ...rest] = types;
  const groups = groupers[type](values);
  const result: Record<string, GroupedData> = {};
  for (const key of Object.keys(groups)) {
    result[key] = groupData(groups[key], rest, groupers);
  }
  return result;
};

interface GenerateData {
  company: string;
  creativeMap: CreativeMap | undefined;
  colorMap: Record<string, string>;
  creativeToImage: Record<string, string>;
  placementMap: Record<string, Placement>;
}

const GenerateDataContext = createContext<GenerateData>({
  company: '',
  creativeMap: undefined,
  colorMap: {},
  creativeToImage: {},
  placementMap: {}
});

interface CollapsingItemProps {
  children: ReactNode;
  newTypes: string[];
  group: GroupedData;
  defaultExpanded?: boolean;
}

const CollapsingItem = ({
  children,
  newTypes,
  group,
  defaultExpanded = false
}: CollapsingItemProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className='collapsing-group'>
      <InteractiveDiv className='collapsing-header' onClick={() => setExpanded(!expanded)}>
        {children}
        {expanded ? <MdExpandLess /> : <MdExpandMore />}
      </InteractiveDiv>
      <Collapse in={expanded} mountOnEnter unmountOnExit>
        <div className={newTypes.length ? 'collapsing-list' : 'final-rows'}>
          {newTypes.length ? (
            <CollapsingGroup types={newTypes} group={group} />
          ) : (
            <div>
              {(Array.isArray(group) ? group : []).map(id => (
                <PlacementRow key={id} id={id} />
              ))}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

interface CollapsingGroupProps {
  types: string[];
  group: GroupedData;
}

const CollapsingGroup = ({ types, group }: CollapsingGroupProps) => {
  if (Array.isArray(group)) {
    return null;
  }

  const [type, ...newTypes] = types;
  const keys = Object.keys(group);
  const sorter = SORTERS[type] ?? ((sortKeys: string[]) => [...sortKeys].sort());

  return (
    <>
      {sorter(keys).map(key => (
        <CollapsingItem
          key={key}
          newTypes={newTypes}
          group={group[key]}
          defaultExpanded={keys.length <= 1}
        >
          <DimensionHeader type={type} value={key} />
        </CollapsingItem>
      ))}
    </>
  );
};

const PlacementRow = ({ id }: { id: string }) => {
  const { placementMap } = useContext(GenerateDataContext);
  const placement = placementMap[id];
  if (!placement) {
    return null;
  }
  return (
    <div className='placement-row'>
      {id} | {placement.name} | {placement.vendor}
    </div>
  );
};

interface RotationHeaderProps {
  rotation: string;
}

const RotationHeader = ({ rotation }: RotationHeaderProps) => {
  const { creativeMap, colorMap } = useContext(GenerateDataContext);
  const [expanded, setExpanded] = useState(false);

  const units = useMemo(
    () =>
      rotation.split('|').map(unit => {
        const [name, value] = unit.split('_');
        return {
          name,
          value: parseInt(value, 10),
          color: colorMap[name] || 'white',
          creativeName: creativeMap?.[name]?.name ?? name
        };
      }),
    [rotation, creativeMap, colorMap]
  );

  const displayItems = useMemo(() => {
    const sorted = [...units].sort(
      (a, b) =>
        b.value - a.value ||
        a.creativeName.localeCompare(b.creativeName) ||
        a.name.localeCompare(b.name)
    );
    return expanded ? sorted : sorted.slice(0, 4);
  }, [units, expanded]);

  return (
    <div className='rotation-header'>
      <PieChart data={units} size={50} />
      <div className={cn('legend-items', { collapsed: !expanded })}>
        {displayItems.map(({ name, value, color, creativeName }) => (
          <div key={name}>
            <div className='swatch' style={{ backgroundColor: color }} />
            <div>{creativeName}</div>
            {expanded && <small>({name})</small>}
            <div>{value}%</div>
          </div>
        ))}
        <InteractiveDiv
          className='expand-button'
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? 'Less' : 'More...'}
        </InteractiveDiv>
      </div>
    </div>
  );
};

interface DimensionHeaderProps {
  type: string;
  value: string;
}

const DimensionHeader = ({ type, value }: DimensionHeaderProps) => {
  const { company, colorMap, creativeToImage } = useContext(GenerateDataContext);
  switch (type) {
    case DIMENSIONS.DATE:
      return (
        <div className='date-header'>
          {value === CURRENT_DATE_LABEL ? value : dateToLongDate(value)}
        </div>
      );
    case DIMENSIONS.NETWORK:
      return (
        <div className='network-header'>
          <Img src={`${CDN_NETWORK_LINK}${encodeURIComponent(value.split(' ')[0])}.png`} />
          <div>{value}</div>
        </div>
      );
    case DIMENSIONS.NETWORK_GROUP:
      return (
        <div className='network-header'>
          <Img src={`${CDN_NETWORK_LINK}${encodeURIComponent(value.toUpperCase())}.png`} />
          <div>{value}</div>
        </div>
      );
    case DIMENSIONS.ROTATION:
      return <RotationHeader rotation={value} />;
    case DIMENSIONS.ISCI:
      return (
        <div className='isci-header'>
          <div className='swatch' style={{ backgroundColor: colorMap[value] || 'white' }} />
          {value}
        </div>
      );
    case DIMENSIONS.CREATIVE:
      return (
        <div className='creative-header'>
          <Img src={getCreativeThumbnail(company, creativeToImage[value])} />
          <div>{value}</div>
        </div>
      );
    default:
      return <div>{value}</div>;
  }
};

export const Generate = () => {
  const { cid: company } = useCompanyInfo();
  const { creativeMap, colorMap } = useCreativeMap({
    company,
    mediaTypes: GENERATE_MEDIA_TYPES
  });

  const creativeToImage = useMemo(() => {
    const map: Record<string, string> = {};
    for (const elem of Object.values(creativeMap ?? {})) {
      map[elem.name] = elem.file;
    }
    return map;
  }, [creativeMap]);

  const { tab, goToTab, shopData, approvalStageData, updateApprovalStage } =
    useContext(StreamingCreativesContext);

  const { placements, placementMap } = shopData;

  const [generating, setGenerating] = useState(false);
  const [cometProgress, setCometProgress] = useState<CometProgress>();

  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  const [filter, setFilter] = useStateFunction<(line: LineMeta) => boolean>(() => true);

  const generateFlashtalking = useCallback(async () => {
    try {
      setGenerating(true);
      setCometProgress(undefined);
      const returnedPlacementMap = await fetchCometSelfInvoke<
        { company: string; placementMap: null },
        Record<string, { error?: string }>
      >(
        FlashtalkingLambdaFetch,
        '/sendCreativeRotationsToFlashtalking',
        { company, placementMap: null },
        {
          timeout: COMET_WORKER_TIMEOUT_SECONDS,
          onProgress: setCometProgress
        }
      );
      const succeeded: string[] = [];
      const failed: string[] = [];
      for (const key of Object.keys(returnedPlacementMap ?? {})) {
        const { error } = returnedPlacementMap[key];
        if (error) {
          failed.push(`${key}: ${error}`);
        } else {
          succeeded.push(key);
        }
      }
      const messageLines: string[] = [];
      if (succeeded.length) {
        messageLines.push(`✓ Updated (${succeeded.length}): ${succeeded.join(', ')}`);
      }
      if (failed.length) {
        messageLines.push(`✗ Failed (${failed.length}):\n${failed.join('\n')}`);
      }
      const getTitle = () => {
        if (!failed.length) return 'Success';
        return succeeded.length ? 'Partial Success' : 'Errors';
      };
      setError({
        title: getTitle(),
        intent: failed.length ? 'error' : 'success',
        message: messageLines.join('\n\n')
      });
      if (failed.length === 0) {
        updateApprovalStage(company, ApprovalStages.PRE_REVIEW, [], '', '');
      }
    } catch (e) {
      setError({ message: `Failed to update Flashtalking. Error: ${e.message}`, reportError: e });
    }
    setGenerating(false);
  }, [company, updateApprovalStage, setError]);

  const [groupableData, placementDatesToRotations] = useMemo(() => {
    const newData: GroupableLine[] = [];
    const datesToRotations: Record<string, string> = {};
    for (const { id, creatives } of placements) {
      const creativesByDate: Record<string, { isci: string; pct: number }[]> = {};
      for (const { isci, pct, start_date } of creatives) {
        const date = start_date <= TODAY ? CURRENT_DATE_LABEL : start_date;
        (creativesByDate[date] ||= []).push({ isci, pct });
        newData.push({ placementID: id, isci, pct, date });
      }
      for (const date of Object.keys(creativesByDate)) {
        datesToRotations[`${date}_${id}`] = makeRotationKey(creativesByDate[date]);
      }
    }
    return [newData, datesToRotations] as const;
  }, [placements]);

  const groupers = useMemo<Groupers>(
    () => ({
      [DIMENSIONS.DATE]: groupBy(line => line.date),
      [DIMENSIONS.NETWORK]: groupBy(line => placementMap[line.placementID]?.network_label ?? ''),
      [DIMENSIONS.NETWORK_GROUP]: groupBy(
        line => placementMap[line.placementID]?.network_group ?? ''
      ),
      [DIMENSIONS.ROTATION]: groupBy(
        line => placementDatesToRotations[`${line.date}_${line.placementID}`] ?? ''
      ),
      [DIMENSIONS.ISCI]: groupBy(line => line.isci),
      [DIMENSIONS.CREATIVE]: groupBy(line => creativeMap?.[line.isci]?.name ?? line.isci)
    }),
    [placementMap, creativeMap, placementDatesToRotations]
  );

  const addLineMetadata = useCallback(
    ({ date, isci, pct, placementID }: GroupableLine): LineMeta => {
      const placement = placementMap[placementID];
      return {
        date: date === CURRENT_DATE_LABEL ? CURRENT_DATE_LABEL : dateToLongDate(date),
        network: placement.network,
        network_label: placement.network_label,
        network_group: placement.network_group,
        isci,
        creative: creativeMap?.[isci]?.name ?? isci,
        pct,
        placementName: placement.name
      };
    },
    [placementMap, creativeMap]
  );

  const [selectedGrouping, setSelectedGrouping] = useState<GroupingKey>('DATE');
  const currentGroupingOrder = PRESETS[GROUPING_OPTIONS[selectedGrouping]];
  const groupedData = useMemo(
    () =>
      groupData(
        groupableData.filter(line => filter(addLineMetadata(line))),
        currentGroupingOrder,
        groupers
      ),
    [groupableData, currentGroupingOrder, groupers, filter, addLineMetadata]
  );

  const generateData = useMemo<GenerateData>(
    () => ({ company, creativeMap, colorMap, creativeToImage, placementMap }),
    [company, creativeMap, colorMap, creativeToImage, placementMap]
  );

  const filterBarLines = useMemo(
    () => groupableData.map(addLineMetadata),
    [groupableData, addLineMetadata]
  );

  // Default to true so the button is not enabled before the fetch finishes.
  const [hasPendingCreatives, setHasPendingCreatives] = useState(true);

  useEffect(() => {
    (async () => {
      if (company) {
        try {
          const res = await StreamingLambdaFetch('/getPendingCreatives', { params: { company } });
          const pendingCreatives = await awaitJSON<Record<string, unknown>>(res);
          setHasPendingCreatives(Boolean(Object.keys(pendingCreatives).length));
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      }
    })();
  }, [company, setError, tab]);

  return (
    <div className='streaming-creatives-card-container'>
      <CometProgressDialog
        show={generating}
        title='Uploading to Flashtalking'
        progress={cometProgress}
      />
      <div className='streaming-creatives-card'>
        <div className='generate'>
          <div className='generate-action generate-view-toggles'>
            {(Object.keys(GROUPING_OPTIONS) as GroupingKey[]).map(key => (
              <Button
                size={Size.LG}
                key={key}
                appearance={selectedGrouping === key ? Appearance.FILLED : Appearance.OUTLINED}
                onClick={() => setSelectedGrouping(key)}
              >
                {GROUPING_OPTIONS[key]}
              </Button>
            ))}
          </div>
          <div className='generate-action'>
            <FilterBar
              size={Size.LG}
              hasAdvanced
              lines={filterBarLines}
              options={FILTER_BAR_OPTIONS}
              onFilter={setFilter}
            />
          </div>
          <div className='generate-content'>
            <GenerateDataContext.Provider value={generateData}>
              <CollapsingGroup group={groupedData} types={currentGroupingOrder} />
            </GenerateDataContext.Provider>
          </div>
          <div className='buttons-container'>
            <Button
              size={Size.LG}
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              onClick={() => goToTab(TabKey.APPROVALS)}
            >
              Back
            </Button>
            <Button
              size={Size.LG}
              loading={generating}
              disabled={approvalStageData?.stage !== ApprovalStages.APPROVED && hasPendingCreatives}
              onClick={() =>
                setAreYouSure({
                  title: "You're sending an update to Flashtalking",
                  message:
                    'You are about to upload these changes directly to Flashtalking. Are you sure you want to continue?',
                  intent: Intent.WARNING,
                  cancelText: 'Never mind',
                  okayText: 'Yes',
                  onOkay: () => generateFlashtalking()
                })
              }
            >
              Upload to Flashtalking
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
